import { State, Action, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { NotificationService } from '@core/services/notification.service';
import { CampaignVoucherDTO } from '@app/sales/models/voucher.model';
import { VoucherService } from '@app/sales/services/voucher.service';
import {
  ClaimOrValidateCampaignErrorAction,
  ClaimOrValidateCampaignSuccessAction,
  ClaimCampaignVoucherAction,
  ValidateCampaignVoucherAction,
  ResetCampaignVoucherAction,
} from './voucher.actions';
import { VoucherHelperService } from '@app/sales/services/voucher-helper.service';

// TODO SAPO-4205: Make error messages seem more professional
enum ErrorMessages {
  NoValidItemsError = 'Ærlig talt, legg til en telefon som koden matcher først please',
  InsufficientAmountError = 'Få på no mersalg før du prøver å bruke denne koden',
}

export interface VoucherStateModel {
  claimedVoucher: CampaignVoucherDTO;
}

const initalVoucherState: VoucherStateModel = {
  claimedVoucher: {
    voucherId: '',
    voucherType: '',
    voucherState: '',
    description: '',
    expiresAt: null,
    voucherAmount: null,
    externalReference: '',
    voucherInvalidReason: null,
    voucherCode: '',
    campaignDetails: null,
    campaignVoucherRequirements: null,
  },
};

@State<VoucherStateModel>({
  name: 'voucher',
  defaults: initalVoucherState,
})
@Injectable()
export class VoucherState {
  private voucherService = inject(VoucherService);
  private voucherHelperService = inject(VoucherHelperService);
  private notificationService = inject(NotificationService);

  @Action(ClaimCampaignVoucherAction)
  public claimCampaignVoucherAction(
    { dispatch }: StateContext<VoucherStateModel>,
    { campaignCode }: ClaimCampaignVoucherAction
  ) {
    if (!campaignCode) {
      return;
    }

    const claimRequest = this.voucherHelperService.buildClaimCampaignVoucherRequest(campaignCode);

    return this.voucherService.claimCampaignVoucher(claimRequest).pipe(
      tap(response => dispatch(new ClaimOrValidateCampaignSuccessAction(response))),
      catchError(error => dispatch(new ClaimOrValidateCampaignErrorAction(error?.status)))
    );
  }

  @Action(ValidateCampaignVoucherAction)
  public validateCampaignVoucherAction({ dispatch, getState }: StateContext<VoucherStateModel>) {
    const voucherId = getState().claimedVoucher?.voucherId;

    if (!voucherId) {
      return;
    }

    const validateRequest = this.voucherHelperService.buildValidateCampaignVoucherRequest();

    return this.voucherService.validateCampaignVoucher(voucherId, validateRequest).pipe(
      tap(response => dispatch(new ClaimOrValidateCampaignSuccessAction(response))),
      catchError(error => dispatch(new ClaimOrValidateCampaignErrorAction(error?.status)))
    );
  }

  @Action(ClaimOrValidateCampaignSuccessAction)
  public claimOrValidateCampaignSuccessAction(
    { patchState }: StateContext<VoucherStateModel>,
    { response }: ClaimOrValidateCampaignSuccessAction
  ) {
    patchState({
      claimedVoucher: response,
    });
  }

  // TODO SAPO-4205: handle errors
  @Action(ClaimOrValidateCampaignErrorAction)
  public claimOrValidateCampaignErrorAction(
    {}: StateContext<VoucherStateModel>,
    { invalidReason }: ClaimOrValidateCampaignErrorAction
  ) {
    this.notificationService.warn(ErrorMessages[invalidReason]);
  }

  @Action(ResetCampaignVoucherAction)
  public resetCampaignVoucherAction({ patchState }: StateContext<VoucherStateModel>) {
    patchState({ claimedVoucher: initalVoucherState.claimedVoucher });
  }
}
